import React from "react";
import {
  ComposedChart,
  Line,
  XAxis,
  YAxis,
  ResponsiveContainer,
  ReferenceLine,
  Tooltip,
} from "recharts";
import { Box, Typography } from "@mui/material";
import { HourlyWeatherData, WeatherForecastResponse } from "../../../lib/Types";
import { WeatherConfig } from "./weatherConfig";
import { toISOStringWithTimezone } from "../../../lib/Util";

interface Props {
  data: WeatherForecastResponse;
  displayRange: {
    start: string;
    stop: string;
  };
  rightMargin: number;
  leftMargin: number;
  yAxisMargin: number;
  weatherConfig: Record<string, WeatherConfig>;
  timezone: string;
  /**
   * When true, the built-in "Weather" label is not rendered. Use this when the
   * caller wants to render a label in a different position (e.g. as a
   * left-aligned overlay aligned with sibling timelines).
   */
  hideLabel?: boolean;
  /**
   * Recharts syncId used to coordinate tooltips/cursor with sibling charts.
   * Defaults to "power-graph" to preserve the SitePowerGraph behavior.
   */
  syncId?: string;
  /**
   * Optional mouse-move handler. Useful for callers that want to drive the
   * sibling chart's tooltip state from the weather strip's hover position.
   */
  onMouseMove?: (ev: { activeLabel?: string | number }) => void;
  /**
   * Optional mouse-leave handler. Pair with `onMouseMove` to clear any state
   * the caller set while the user was hovering the strip.
   */
  onMouseLeave?: () => void;
}

const WeatherTimeline: React.FC<Props> = ({
  data,
  displayRange,
  rightMargin,
  leftMargin,
  yAxisMargin,
  weatherConfig,
  timezone,
  hideLabel = false,
  syncId = "power-graph",
  onMouseMove,
  onMouseLeave,
}) => {
  if (!data?.hourly) return null;

  // add unix time x series for compatibility with other graphs
  const chartData = data.hourly.map((item) => ({
    ...item,
    // Scale weather API returns local timestring without timezone, so we need to add it before converting to unix time
    unixTime: new Date(toISOStringWithTimezone(item.time, timezone)).getTime(),
  }));

  const unixStart = new Date(displayRange.start).getTime();
  const unixStop = new Date(displayRange.stop).getTime();

  const getYAxes = () => {
    return Object.values(weatherConfig).map((config) => (
      <YAxis
        key={config.field}
        yAxisId={config.field}
        domain={[config.min, config.max]}
        width={yAxisMargin}
        type="number"
        hide={true}
      />
    ));
  };

  const getLines = () => {
    return Object.values(weatherConfig).map((config) => (
      <Line
        key={config.field}
        yAxisId={config.field}
        type="linear"
        dataKey={config.field}
        stroke={config.color}
        strokeWidth={1}
        dot={false}
        isAnimationActive={false}
      />
    ));
  };

  const createReferenceLine = (value: number) => (
    <ReferenceLine
      key={`ref-${value}`}
      y={value}
      stroke="#808080"
      strokeOpacity="10%"
      strokeDasharray="4 4"
      strokeWidth={1}
      // cloud cover is always 0-100%, so we can use this metric for the reference lines.
      yAxisId="cloud_cover"
    />
  );

  // Recharts cartesian grid does not render lines for small graphs so we need to craete them manually.
  const getReferenceLines = () => {
    const values = [0, 25, 50, 75, 100];
    return values.map(createReferenceLine);
  };

  return (
    <Box>
      {!hideLabel && (
        <Typography
          variant="overline"
          sx={{ marginLeft: leftMargin + "px", textTransform: "none", fontWeight: 400 }}
        >
          Weather
        </Typography>
      )}
      <ResponsiveContainer width="100%" height={60}>
        <ComposedChart
          syncId={syncId}
          data={chartData}
          margin={{
            top: 5,
            right: rightMargin,
            left: leftMargin,
            bottom: 15,
          }}
          onMouseMove={onMouseMove}
          onMouseLeave={onMouseLeave}
        >
          <XAxis
            dataKey="unixTime"
            domain={[unixStart, unixStop]}
            type="number"
            hide={true}
            allowDataOverflow={true}
          />
          {getYAxes()}
          {getReferenceLines()}
          {getLines()}
          <Tooltip content={<></>} />
        </ComposedChart>
      </ResponsiveContainer>
    </Box>
  );
};

export default WeatherTimeline;
